import { cn } from "@/lib/utils";
import { forwardRef } from "react";
import { Badge } from "./badge";
import { TextChip } from "./chip";

// ============================================
// 타입 정의
// ============================================

type SidebarMenuSize = "base" | "small";
type SidebarMenuPressedColor = "neutral" | "primary" | "blue";

interface SidebarMenuProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size?: SidebarMenuSize;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  /** 빨간 점 상태 인디케이터 */
  badge?: boolean;
  /** 칩 컴포넌트 또는 텍스트 (string이면 TextChip으로 렌더링) */
  chip?: React.ReactNode;
  /** 눌린 상태 (선택된 메뉴) */
  isPressed?: boolean;
  /** 눌린 상태 색상 */
  pressedColor?: SidebarMenuPressedColor;
  children: React.ReactNode;
}

// ============================================
// 스타일 정의
// ============================================

const baseStyles = cn(
  "group relative flex w-full items-center rounded-small px-2 py-1",
  "text-primary-base transition-colors",
  "hover:bg-slate-50",
  "disabled:opacity-[0.52] disabled:pointer-events-none",
);

const sizeStyles: Record<SidebarMenuSize, string> = {
  base: "gap-2 body-2-normal-medium",
  small: "gap-1 label-2-medium",
};

const labelSizeStyles: Record<SidebarMenuSize, string> = {
  base: "body-2-normal-medium",
  small: "label-2-medium",
};

const pressedStyles: Record<SidebarMenuPressedColor, string> = {
  neutral: "bg-slate-100 active:bg-slate-100",
  primary: "bg-primary-base hover:bg-primary-base active:bg-primary-base",
  // Figma Decorate/Interaction/Blue(#2b7fff) 12% — 선택된 메뉴 강조
  blue: "bg-blue-500/12 hover:bg-blue-500/12 active:bg-blue-500/12",
};

// ============================================
// SidebarMenu 컴포넌트
// ============================================

export const SidebarMenu = forwardRef<HTMLButtonElement, SidebarMenuProps>(
  (
    {
      size = "base",
      leftIcon,
      rightIcon,
      badge = false,
      chip,
      isPressed = false,
      pressedColor = "neutral",
      className,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          baseStyles,
          sizeStyles[size],
          isPressed ? pressedStyles[pressedColor] : "active:bg-slate-100",
          className,
        )}
        {...props}
      >
        {/* Left Icon */}
        {leftIcon && (
          <span className="flex items-center justify-center size-4 shrink-0">
            {leftIcon}
          </span>
        )}

        {/* Label + Chip: 칩은 오른쪽 끝 정렬이 의도. 한 그룹으로 묶어
            버튼의 gap이 라벨-칩 사이에 강제 여백을 넣지 않게 한다 */}
        <span className="flex flex-1 min-w-0 items-center">
          <span
            className={cn(
              "flex-1 truncate text-left",
              labelSizeStyles[size],
              isPressed && pressedColor === "primary"
                ? "text-static-white"
                : "text-primary",
            )}
          >
            {children}
          </span>

          {/* Chip (Beta 등) - string이면 TextChip, 아니면 그대로 렌더링 */}
          {chip && (
            <span className="flex items-center shrink-0">
              {typeof chip === "string" ? (
                <TextChip
                  className={
                    isPressed
                      ? "bg-slate-600 text-white"
                      : "bg-slate-200 text-slate-600"
                  }
                >
                  {chip}
                </TextChip>
              ) : (
                chip
              )}
            </span>
          )}
        </span>

        {/* Right Icon */}
        {rightIcon && (
          <span className="flex items-center justify-center size-4 shrink-0">
            {rightIcon}
          </span>
        )}

        {/* Badge (빨간 점) */}
        {badge && <Badge />}
      </button>
    );
  },
);

SidebarMenu.displayName = "SidebarMenu";

export default SidebarMenu;
